import { Component, computed, DestroyRef, inject } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { MatButtonModule } from '@angular/material/button';
import { MatDialogContent, MatDialogRef } from '@angular/material/dialog';
import { MatDividerModule } from '@angular/material/divider';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatStepperModule } from '@angular/material/stepper';

import { ProfileService } from '@tfs/auth';
import { LOCALE_TO_DATE_FORMAT } from '@tfs/date-format';

import { UserOnboardingProfileDataStore } from '../data-access/user-onboarding-profile.data-store';
import { OnboardingStatusDto } from '../models/user-onboarding-modal.models';
import { UserOnboardingDataComponent } from '../ui-user-onboarding-modal/user-onboarding-data/user-onboarding-data.component';
import { UserOnboardingMeasurementSystemComponent } from '../ui-user-onboarding-modal/user-onboarding-measurement-system/user-onboarding-measurement-system.component';

const WIZARD_STEPS = [
  {
    status: OnboardingStatusDto.Invited,
    label: 'Welcome',
    subLabel: 'Introduction to Toolhive',
  },
  {
    status: OnboardingStatusDto.MeasurementSystemStep,
    label: 'Measurement system',
    subLabel: 'Select your preferred units',
  },
  {
    status: OnboardingStatusDto.DataPreferenceStep,
    label: 'Data preference',
    subLabel: 'Configure data settings',
  },
] as const;

@Component({
  selector: 'tfs-user-onboarding-modal',
  imports: [
    MatButtonModule,
    MatDialogContent,
    MatDividerModule,
    MatProgressSpinnerModule,
    MatStepperModule,
    UserOnboardingMeasurementSystemComponent,
    UserOnboardingDataComponent,
  ],
  templateUrl: './user-onboarding-modal.component.html',
  styleUrl: './user-onboarding-modal.component.scss',
})
export class UserOnboardingModalComponent {
  private readonly profileService = inject(ProfileService);
  private readonly profileDataStore = inject(UserOnboardingProfileDataStore);
  private readonly dialogRef = inject(MatDialogRef<UserOnboardingModalComponent>);
  private readonly destroyRef = inject(DestroyRef);

  protected readonly onboardingStatus = OnboardingStatusDto;
  protected readonly isSaving = this.profileDataStore.isLoading;

  protected readonly currentStepIndex = computed(() => {
    const status = this.profileService.userProfile()?.onboardingStatus;

    if (status === OnboardingStatusDto.Onboarded) {
      return WIZARD_STEPS.length - 1;
    }

    const index = WIZARD_STEPS.findIndex(step => step.status === status);
    return index >= 0 ? index : 0;
  });

  protected advanceToStatus(targetStatus: OnboardingStatusDto): void {
    this.profileDataStore
      .updateOnboardingStatus(targetStatus)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => {
        if (targetStatus === OnboardingStatusDto.Onboarded) {
          const locale = this.profileService.userProfile()?.locale ?? 'de-DE';
          this.setDateFormat(LOCALE_TO_DATE_FORMAT[locale]);
        }
      });
  }

  protected setDateFormat(dateFormat: string): void {
    this.profileDataStore
      .updateDateFormat(dateFormat)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => {
        this.closeAndReload();
      });
  }

  private closeAndReload(): void {
    this.dialogRef.close(true);
    window.location.href = `${window.location.protocol}//${window.location.host}`;
  }
}
